import {useEffect} from 'react';
import {isAxiosError} from 'axios';
import {useQuery} from '@tanstack/react-query';
import {getWhoamiQueryOptions} from '@api/auth/auth';

import {getToken} from '@/lib/auth-token';
import {useAuthStore} from '@/store/auth';
import type {WhoamiResponse} from '@/lib/auth-types';

export function useAuth() {
  // Use `useQuery` with the orval-generated query options helper rather than
  // the wrapping `useWhoami` hook: the wrapper's option type is
  // overly strict (requires queryKey at compile-time even though the helper
  // supplies it at runtime), so passing `enabled` / `retry` overrides
  // through the wrapper triggers a spurious type error.
  //
  // Two overrides applied:
  // - `enabled: !!getToken()` — without a token, /login, /forgot-password,
  //   /reset-password (which all mount this hook via __root → useAuth)
  //   would each fire a guaranteed-401 whoami. Whoami is in the axios
  //   interceptor's 401-skip list, so the error propagates to the effect
  //   below; previously that wiped the auth store on every guest-route
  //   visit.
  // - `retry: false` — retrying a 401 does nothing (the token is bad, not
  //   the network) and just extends the window where `error` is set.
  const {
    data: rawUser,
    isLoading,
    error,
  } = useQuery({
    ...getWhoamiQueryOptions(),
    enabled: !!getToken(),
    retry: false,
  });

  const setUser = useAuthStore((s) => s.setUser);
  const clearUser = useAuthStore((s) => s.clearUser);

  // The generated hook already returns the enriched shape; `WhoamiResponse`
  // widens it by the one field the spec has yet to carry (see auth-types.ts),
  // so this needs no cast.
  const user: WhoamiResponse | undefined = rawUser;

  useEffect(() => {
    if (user) {
      setUser(user);
      return;
    }
    // Only clear on a confirmed 401 — that's the only error that means
    // "this token is bad, log the user out". Transient errors (network
    // failure, 5xx, abort) leave the cached user untouched so the next
    // interaction can recover. Prior code cleared on any isError, which
    // produced spurious logouts when React Query's normal lifecycle
    // (refetches, focus events, retry races) briefly surfaced an error.
    if (isAxiosError(error) && error.response?.status === 401) {
      clearUser();
    }
  }, [user, error, setUser, clearUser]);

  // Derive auth state directly from query data — not from Zustand.
  // The Zustand store is updated via useEffect (runs after render), so
  // reading isAuthenticated from the store on the first render after login
  // returns stale `false` and causes the auth guard to redirect to /login.
  // Deriving from `user` is synchronous with the query cache and avoids
  // the one-render-behind race condition.
  const isAuthenticated = !!user;
  const isSuperAdmin = user?.is_super_admin ?? false;

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated,
    isSuperAdmin,
  };
}
